import { useCallback, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../services/api';
import { onAngularEvent, trackEvent } from '../../services/angular-bridge';
import { notifySuccess } from '../../services/notify';

export interface AuditLogEntry {
  id: string;
  date: number;
  user: string;
  accountId: string;
  projectId: string;
  ipAddress: string;
  actionDescription: string;
  actionDetails: Record<string, unknown>;
  authenticated: boolean;
}

export interface AuditLogPage {
  records: AuditLogEntry[];
  totalRecords: number;
  totalPageCount: number;
}

/**
 * Records a user action in the project's audit trail. Returns a fire-and-forget
 * function: the trail must never block or interrupt the action it records, so
 * failures are swallowed rather than surfaced.
 *
 * `description` becomes part of the URL path, and the details object the request body.
 */
export function useAuditAction(projectId: string | undefined) {
  return useCallback(
    (description: string, details: Record<string, unknown>) => {
      if (!projectId) return;
      void api
        .post(`/auditing/project/${projectId}/description/${encodeURIComponent(description)}`, details)
        .catch(() => {});
    },
    [projectId]
  );
}

/** Strips the pre-signed query string, leaving the stable object path for the trail. */
export function auditUri(url: string | undefined | null): string | undefined {
  return url ? url.split('?')[0] : undefined;
}

/**
 * The audit log CSV's own completion toast (FeedService.AuditLogDownloadComplete). Moved here
 * from Angular's AuditLogService; mounted once by WebSocketBridge.
 *
 * Distinct from FeedService.ReportComplete, which useReportEvents owns — the audit CSV is
 * requested through /report but announces itself on this separate event.
 */
export function useAuditLogEvents() {
  useEffect(() => {
    return onAngularEvent('FeedService.AuditLogDownloadComplete', (message: { directDownloadPath?: string }) => {
      notifySuccess({
        title: 'Download Complete',
        description: 'The audit log CSV is available for download',
        buttons: `<a class="btn btn-primary" href="${message?.directDownloadPath}"><i class="icon-download-alt"></i> Download</a>`,
        timeout: 5000,
      });
    });
  }, []);
}

// The generic FeedService.ReportComplete toast used to be duplicated here; it is now owned
// once, globally, by useReportEvents.
export function useGenerateAuditReport(projectId: string) {
  return useMutation({
    mutationFn: () => {
      trackEvent('download audit log report', 'clicked', projectId);
      notifySuccess({
        title: 'Report started',
        description: 'Name: Audit Log Report',
        timeout: 5000,
      });
      return api.post(`/project/${projectId}/report`, {
        projectId,
        type: 'audit_log_report',
        name: 'Audit Log Report',
        format: 'csv',
        multiValueCharCode: '0',
        dateTimeFormat: 'yyyy-MM-dd',
        hasHeaderRow: false,
        fields: [],
      });
    },
  });
}
